import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const masterConfig = {
  cities: { 
    title: 'Cities', 
    singular: 'City',
    endpoint: 'cities',
    hasCountry: true,
    hasState: true,
    columns: ['Name', 'Country', 'State', 'Description', 'Status']
  },
  states: { 
    title: 'States', 
    singular: 'State',
    endpoint: 'states',
    hasCountry: true,
    hasState: false,
    columns: ['Name', 'Country', 'Description', 'Status']
  },
  countries: { 
    title: 'Countries', 
    singular: 'Country',
    endpoint: 'countries',
    hasCountry: false,
    hasState: false,
    isCountry: true,
    columns: ['Name', 'Flag', 'Currency', 'Code', 'Description', 'Status']
  },
  categories: { 
    title: 'Categories', 
    singular: 'Category',
    endpoint: 'categories',
    hasCountry: false,
    hasState: false,
    hasImage: true,
    columns: ['Name', 'Image', 'Description', 'Status']
  },
  tags: { 
    title: 'Tags', 
    singular: 'Tag',
    endpoint: 'tags',
    hasCountry: false,
    hasState: false,
    columns: ['Name', 'Description', 'Status']
  },
  allergens: { 
    title: 'Allergens', 
    singular: 'Allergen',
    endpoint: 'allergens',
    hasCountry: false,
    hasState: false,
    columns: ['Name', 'Description', 'Status']
  },
  'heat-profiles': { 
    title: 'Heat Profiles', 
    singular: 'Heat Profile',
    endpoint: 'heat-profiles',
    hasCountry: false,
    hasState: false,
    columns: ['Name', 'Description', 'Status']
  },
  'dietary-restrictions': { 
    title: 'Dietary Restrictions', 
    singular: 'Dietary Restriction',
    endpoint: 'dietary-restrictions',
    hasCountry: false,
    hasState: false,
    hasImage: true,
    columns: ['Name', 'Image', 'Description', 'Status']
  },
};

const MasterList = () => {
  const { type } = useParams();
  const config = masterConfig[type] || { title: 'Items', singular: 'Item', endpoint: type, columns: ['Name', 'Description', 'Status'] };
  
  const [items, setItems] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [filteredStates, setFilteredStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    description: '', 
    country_id: '',
    state_id: '',
    flag: '',
    currency: '',
    country_code: '',
    image_url: '',
    is_active: true 
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchItems();
    if (config.hasCountry) {
      fetchCountries();
    }
    if (config.hasState) {
      fetchAllStates();
    }
  }, [type]);

  // Filter states when country changes
  useEffect(() => {
    if (formData.country_id && states.length > 0) {
      const filtered = states.filter(s => s.country_id === formData.country_id);
      setFilteredStates(filtered);
    } else {
      setFilteredStates([]);
    }
  }, [formData.country_id, states]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/${config.endpoint}`, {
        withCredentials: true
      });
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch items:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/countries`, {
        withCredentials: true
      });
      setCountries(response.data.filter(c => c.is_active));
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const fetchAllStates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/states`, {
        withCredentials: true
      });
      setStates(response.data.filter(s => s.is_active));
    } catch (error) {
      console.error('Failed to fetch states:', error);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({ 
        name: item.name, 
        description: item.description || '', 
        country_id: item.country_id || '',
        state_id: item.state_id || '',
        flag: item.flag || '',
        currency: item.currency || '',
        country_code: item.country_code || '',
        image_url: item.image_url || '',
        is_active: item.is_active 
      });
    } else {
      setEditingItem(null);
      setFormData({ name: '', description: '', country_id: '', state_id: '', flag: '', currency: '', country_code: '', image_url: '', is_active: true });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        is_active: formData.is_active
      };
      
      if (config.hasCountry) {
        payload.country_id = formData.country_id;
      }
      if (config.hasState) {
        payload.state_id = formData.state_id;
      }
      if (config.isCountry) {
        payload.flag = formData.flag || null;
        payload.currency = formData.currency || null;
        payload.country_code = formData.country_code || null;
      }
      if (config.hasImage) {
        payload.image_url = formData.image_url || null;
      }

      if (editingItem) {
        await axios.put(
          `${API_URL}/api/admin/${config.endpoint}/${editingItem.id}`,
          payload,
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API_URL}/api/admin/${config.endpoint}`,
          payload,
          { withCredentials: true }
        );
      }
      setDialogOpen(false);
      fetchItems();
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Are you sure you want to delete this ${config.singular.toLowerCase()}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/${config.endpoint}/${id}`, {
        withCredentials: true
      });
      fetchItems();
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  const handleCountryChange = (value) => {
    setFormData({ ...formData, country_id: value, state_id: '' });
  };

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const canSave = () => {
    if (!formData.name) return false;
    if (config.hasCountry && !formData.country_id) return false;
    if (config.hasState && !formData.state_id) return false;
    return true;
  };

  return (
    <div data-testid={`master-${type}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-text-primary mb-1">{config.title}</h1>
          <p className="font-body text-text-secondary">Manage {config.title.toLowerCase()} master data</p>
        </div>
        <Button 
          onClick={() => handleOpenDialog()}
          className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-xl gap-2"
          data-testid="add-item-btn"
        >
          <Plus size={18} />
          Add {config.singular}
        </Button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl p-4 border border-surface-border mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" size={18} />
          <Input
            placeholder={`Search ${config.title.toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-xl border-surface-border"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-surface-border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-text-muted">
            <p className="font-body">No {config.title.toLowerCase()} found</p>
            <Button 
              variant="link" 
              onClick={() => handleOpenDialog()}
              className="text-brand-primary"
            >
              Add your first {config.singular.toLowerCase()}
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-background-secondary">
                {config.columns.map(col => (
                  <TableHead key={col} className="font-heading font-semibold">{col}</TableHead>
                ))}
                <TableHead className="font-heading font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-body font-medium">{item.name}</TableCell>
                  {config.isCountry && (
                    <>
                      <TableCell className="font-body text-lg">{item.flag || '-'}</TableCell>
                      <TableCell className="font-body text-text-secondary">{item.currency || '-'}</TableCell>
                      <TableCell className="font-body text-text-secondary">{item.country_code || '-'}</TableCell>
                    </>
                  )}
                  {config.hasImage && (
                    <TableCell>
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.name} className="w-8 h-8 rounded-lg object-cover border border-surface-border" onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : '-'}
                    </TableCell>
                  )}
                  {config.hasCountry && (
                    <TableCell className="font-body text-text-secondary">{item.country_name || '-'}</TableCell>
                  )}
                  {config.hasState && (
                    <TableCell className="font-body text-text-secondary">{item.state_name || '-'}</TableCell>
                  )}
                  <TableCell className="font-body text-text-secondary">{item.description || '-'}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      item.is_active 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {item.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(item)}
                        className="text-text-secondary hover:text-brand-primary"
                      >
                        <Pencil size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(item.id)}
                        className="text-text-secondary hover:text-red-600"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingItem ? 'Edit' : 'Add'} {config.singular}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Country-specific fields: Flag, Currency, Code */}
            {config.isCountry && (
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="flag" className="font-body font-medium">Flag</Label>
                  <Input
                    id="flag"
                    value={formData.flag}
                    onChange={(e) => setFormData({ ...formData, flag: e.target.value })}
                    placeholder="🇮🇳"
                    className="rounded-xl text-center text-lg"
                    data-testid="country-flag-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="currency" className="font-body font-medium">Currency</Label>
                  <Input
                    id="currency"
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    placeholder="INR"
                    className="rounded-xl"
                    data-testid="country-currency-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country_code" className="font-body font-medium">Code</Label>
                  <Input
                    id="country_code"
                    value={formData.country_code}
                    onChange={(e) => setFormData({ ...formData, country_code: e.target.value })}
                    placeholder="+91"
                    className="rounded-xl"
                    data-testid="country-code-input"
                  />
                </div>
              </div>
            )}

            {/* Image URL - for types with hasImage */}
            {config.hasImage && (
              <div className="space-y-2">
                <Label htmlFor="image_url" className="font-body font-medium">Image URL</Label>
                <Input
                  id="image_url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://example.com/image.png"
                  className="rounded-xl"
                  data-testid="master-image-url-input"
                />
                {formData.image_url && (
                  <img src={formData.image_url} alt="Preview" className="w-12 h-12 rounded-lg object-cover border border-surface-border" onError={(e) => { e.target.style.display = 'none'; }} />
                )}
              </div>
            )}

            {/* Country Select - for States and Cities */}
            {config.hasCountry && (
              <div className="space-y-2">
                <Label className="font-body font-medium">Country <span className="text-red-500">*</span></Label>
                <Select 
                  value={formData.country_id} 
                  onValueChange={handleCountryChange}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Select a country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map(country => (
                      <SelectItem key={country.id} value={country.id}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {countries.length === 0 && (
                  <p className="text-xs text-amber-600">No countries found. Please add countries first.</p>
                )}
              </div>
            )}

            {/* State Select - for Cities only */}
            {config.hasState && (
              <div className="space-y-2">
                <Label className="font-body font-medium">State <span className="text-red-500">*</span></Label>
                <Select 
                  value={formData.state_id} 
                  onValueChange={(value) => setFormData({ ...formData, state_id: value })}
                  disabled={!formData.country_id}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder={formData.country_id ? "Select a state" : "Select country first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredStates.map(state => (
                      <SelectItem key={state.id} value={state.id}>
                        {state.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.country_id && filteredStates.length === 0 && (
                  <p className="text-xs text-amber-600">No states found for this country. Please add states first.</p>
                )}
              </div>
            )}

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="font-body font-medium">Name <span className="text-red-500">*</span></Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={`Enter ${config.singular.toLowerCase()} name`}
                className="rounded-xl"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description" className="font-body font-medium">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter description (optional)"
                className="rounded-xl"
              />
            </div>

            {/* Active */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded border-surface-border"
              />
              <Label htmlFor="is_active" className="font-body">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving || !canSave()}
              className="bg-brand-primary hover:bg-brand-primary-hover rounded-xl"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MasterList;
